package com.solevo.dist.util

import kotlinx.datetime.Instant
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

/**
 * Centralized date formatting utilities for the application.
 * Provides consistent date formatting across all ViewModels and pages.
 */
object DateFormatter {
    
    /**
     * Formats date as "Jan 15, 2024" (short month name, day, year)
     * Used in: Orders, Quotes, OrderDetails tables
     */
    fun formatDateOrDash(date: Instant?): String =
        date?.toLocalDateTime(TimeZone.currentSystemDefault())?.let { ldt ->
            val month = ldt.month.name.lowercase().replaceFirstChar { it.titlecase() }.take(3)
            "$month ${ldt.dayOfMonth}, ${ldt.year}"
        } ?: "—"
    
    /**
     * Formats date as "15 January 2024" (day, full month name, year)
     * Used in: InvoiceDetails, TransactionDetails, QuoteDetails, DeliveryNote
     */
    fun formatAsDayMonthYear(date: Instant?): String {
        return date?.toLocalDateTime(TimeZone.currentSystemDefault())?.let { ldt ->
            val monthFull = ldt.month.name.lowercase().replaceFirstChar { it.titlecase() }
            "${ldt.dayOfMonth} ${monthFull} ${ldt.year}"
        } ?: "—"
    }
    
    /**
     * Formats date as "15 Jan 2024" (day, short month name, year)
     * Used in: Statements table, OrderDetails invoices tab
     */
    fun formatDateShort(date: Instant?): String =
        date?.toLocalDateTime(TimeZone.currentSystemDefault())?.let { ldt ->
            val month = ldt.month.name.lowercase().replaceFirstChar { it.titlecase() }.take(3)
            "${ldt.dayOfMonth} $month ${ldt.year}"
        } ?: "—"
    
    /**
     * Formats date as "15 January, 2024" (day, full month name with comma, year)
     * Used in: OrderDetails delivery notes tab
     */
    fun formatDateLong(date: Instant?): String =
        date?.toLocalDateTime(TimeZone.currentSystemDefault())?.let { ldt ->
            val month = ldt.month.name.lowercase().replaceFirstChar { it.titlecase() }
            "${ldt.dayOfMonth} $month, ${ldt.year}"
        } ?: "—"
    
    /**
     * Formats LocalDate as "2024-01-15" (ISO format for input fields)
     * Used in: StatementPage date inputs
     */
    fun formatDateInput(date: LocalDate): String {
        val y = date.year.toString().padStart(4, '0')
        val m = date.monthNumber.toString().padStart(2, '0')
        val d = date.dayOfMonth.toString().padStart(2, '0')
        return "$y-$m-$d"
    }
}

